<template>
  <div>
    <modal-vue :visible.sync="is_visible" :content_loaded="true">
      <template v-slot:header>
        {{ t(`components.calendars.event_modal.${event ? "edit" : "add"}_title`) }}
      </template>

      <template v-slot:body>
        <div class="form-group">
          <label>{{ t("components.calendars.event_modal.name") }}</label>
          <input
            class="form-control"
            v-model="event_title"
            :placeholder="t('components.calendars.event_modal.name')"
          />
        </div>
        <div class="form-group" v-if="!event">
          <label for="calendar_selected_input">{{
            t("components.calendars.event_modal.calendar_selected_input")
          }}</label>
          <select
            v-model="selected_calendar"
            class="form-control sl"
            id="calendar_selected_input"
          >
            <option v-for="calendar in editable_calendars" v-bind:key="calendar.id" :value="calendar.id">
              {{ calendar.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>{{ t("components.calendars.event_modal.start_date_input") }}</label>
          <div class="d-flex">
            <input
              class="form-control"
              type="date"
              v-model="start_date"
            />
            <input
              class="form-control"
              type="time"
              v-model="start_time"
            />
          </div>
        </div>
        <div class="form-group">
          <div class="form-check">
            <input
              type="checkbox"
              v-model="long_event"
              class="form-check-input"
              id="long_event_checkbox"
              @change="long_event_changed($event)"
            />
            <label class="form-check-label" for="long_event_checkbox">
              {{ t("components.calendars.event_modal.long_event_checkbox") }}
            </label>
          </div>
        </div>
        <div class="form-group" v-if="long_event">
          <label>{{ t("components.calendars.event_modal.end_date_input") }}</label>
          <div class="d-flex">
            <input
              class="form-control"
              type="date"
              v-model="end_date"
            />
            <input
              class="form-control"
              type="time"
              v-model="end_time"
            />
          </div>
        </div>
        <forms-messages-list-vue :messages="errors" v-if="errors"/>
      </template>

      <template v-slot:footer>
        <button class="btn btn-primary" @click="save">
          {{ t("components.calendars.event_modal.save") }}
        </button>
        <button class="btn btn-secondary" @click="hide_modal">
          {{ t("cancel") }}
        </button>
        <button v-if="event" class="btn btn-secondary" @click="remove_event">
          {{ t("components.calendars.event_modal.delete") }}
        </button>
      </template>
    </modal-vue>
  </div>
</template>

<script>
import mixins from "common/mixins"
import { mapActions, mapGetters } from "vuex"
import Validator from "common/Validator"
import moment from "moment"
import { v4 as uuidv4 } from 'uuid';

export default {
  mixins: [mixins.requests, mixins.translate],
  name: "calendars-event-modal-vue",
  components: { "forms-messages-list-vue": () => import(/* webpackChunkName: "[request]" */ "components/forms/MessagesList") },
  props: ["event", "input_date", "is_visible"],
  data() {
    return {
      event_title: "",
      start_date: "",
      start_time: "00:00",
      end_date: "",
      end_time: "00:00",
      long_event: false,
      selected_calendar: null,
      selected_event: null,
      errors: null
    }
  },
  computed: {
    ...mapGetters("calendars", ["editable_calendars"]),
  },
  methods: {
    hide_modal() {
      this.reset_inputs()
      this.$emit("update:is_visible", false)
    },
    validate_form() {
      let validations = [
        {
          fields: I18n.t("components.calendars.event_modal.name"),
          values: this.event_title,
          validates: "filled",
        },
        {
          fields: I18n.t(
            "components.calendars.event_modal.start_date_input"
          ),
          values: this.start_date,
          validates: "filled",
        },
      ]
      if (this.long_event) {
        validations.push({
          fields: I18n.t("components.calendars.event_modal.end_date_input"),
          values: this.end_date,
          validates: "filled",
        })
      }
      if (!this.event) {
        validations.push({
          fields: I18n.t("components.calendars.event_modal.calendar_selected_input"),
          values: this.selected_calendar,
          validates: "filled"
        })
      }
      let validator = new Validator(validations).do()

      this.set_errors(validator.messages)
      return validator.success
    },
    save() {
      let start = new Date(`${this.start_date}T${this.start_time || "00:00"}`)
      let end = new Date(`${this.end_date}T${this.end_time || "00:00"}`)

      if (this.validate_form()) {
        let event = {
          id: this.event ? this.event.id : uuidv4(),
          title: this.event_title,
          start: start,
          end: end,
          allDay: this.start_time && this.start_time != "00:00" ? "false" : "true"
        }
        if (this.event)
          this.update_event({ calendar_id: this.selected_calendar, event: event })
        else
          this.create_event({ calendar_id: this.selected_calendar, event: event })
        this.hide_modal()
      }
    },
    set_errors(errors) {
      this.errors = errors
    },
    reset_inputs() {
      this.event_title = "",
      this.start_date = "",
      this.start_time = "00:00",
      this.end_date = "",
      this.end_time = "00:00"
      this.long_event = false
    },
    remove_event() {
      this.delete_event({
        calendar_id: this.selected_calendar,
        event: {
          id: this.event.id,
        },
      })
      this.hide_modal()
    },
    long_event_changed(event) {
      if (!event.target.checked) {
        this.end_date = ""
        this.end_time = "00:00"
      }
    },
    ...mapActions("calendars", ["create_event", "update_event", "delete_event"]),
  },
  beforeUpdate() {
    this.errors = null
    if (this.event) {
      this.selected_calendar = this.event.extendedProps.calendar_id
      this.start_date = moment(this.event.start).format("YYYY-MM-DD")
      this.start_time = moment(this.event.start).format("HH:mm")
      this.event_title = this.event.title
      this.long_event = this.event.end != null && this.event.end != ""
      if (this.long_event) {
        this.end_date = moment(this.event.end).format("YYYY-MM-DD")
        this.end_time = moment(this.event.end).format("HH:mm")
      }
    } else {
      this.start_date = this.input_date
    }
  },
}
</script>
